import { useLogto } from "@logto/react";
import { Box, CssBaseline, Stack, Typography, useTheme } from "@mui/material";
import GearImgSvg from "assets/images/settingsImg.svg";
import refreshLottie from "assets/lottie/loader.json";
import { Button, CustomHelmet } from "components";
// Internal KB promo — retired, kept for reference.
// import { MainPageBanner } from "components/banner/MainPageBanner";
import { ErrorLayout } from "components/error-screen/ErrorLayout";
import {
  addAttributeToIntercom,
  useCheckUserAuth,
  useFetchAllEmails,
  useFetchCurrentResourceRoleAndPermission,
  useGetBrandingLogo,
  useGetFeatureFlags,
  useGetIntercomHash,
  useGetMspById,
  useGetResourceById,
  useGlobalStore,
  useLogoutAccount,
  useNonPersistantMainStore,
} from "controller";
import { useGetMspGeneralConfig } from "controller/hooks/configurations/general";
import type { FeatureProps } from "controller/store/feature-flag/featureFlagSlice";
import { usePermissionStore } from "controller/store/PermissionStore";
import { resetAllStores } from "controller/store/resetStoreHelper";
import Lottie from "lottie-react";
import {
  ExpiredSubscriptionBanner,
  PricingPageFrame,
} from "pages/modules/control-center/subscription/components/pricing";
import { OverviewPricing } from "pages/modules/control-center/subscription/components/subscription-details/OverviewPricing";
import { Onboarding } from "pages/modules/onboarding-new";
import { type ReactNode, useEffect, useState } from "react";
import { type BrandingLogoResponse, PERMISSIONS, RESOURCE_TYPES } from "shared";

import {
  dayjs,
  identifyHeapUser,
  mixpanel,
  resetHeapIdentity,
  Sentry,
  setHeapAccountProperties,
  setHeapUserProperties,
} from "utilz";
import { debugConsole, emptyStringHandler } from "utilz/helpers";
import { RedirectToDomainLoader } from "./component/RedirectToDomainLoader";
import { TopBarProfileButton } from "./component/top-bar/top-bar-profile-button";

const signoutBroadcast = new BroadcastChannel("signout_channel");

export const MainWrapper = ({ children }: { children: ReactNode }) => {
  const { data: intercomHashData } = useGetIntercomHash();
  const { signOut } = useLogto();

  // Globals

  // fetch global datas
  const [isRedirecting, setIsRedirecting] = useState({
    isRedirecting: false,
    isVanity: false,
    url: "",
  });
  const resourceId = useGlobalStore((state) => state.resourceId);

  const [loggedIn, setLoggedIn] = useState(true);
  const setRefreshing = useGlobalStore((state) => state.setIsRefreshing);
  const setDateFormat = useGlobalStore((state) => state.setDateFormat);
  const role = useGlobalStore((state) => state.role);
  const setIsResourceTimeZone = useGlobalStore(
    (state) => state.setIsResourceTimeZone,
  );
  const resetProfile = useGlobalStore((state) => state.resetCredentials);
  const setGeneralConfig = useGlobalStore((state) => state.setGeneralConfig);
  const setDefaultData = useGlobalStore((state) => state.setDefaultData);
  const setIsTicketTImeFormat24Hour = useGlobalStore(
    (state) => state.setIsTicketTImeFormat24Hour,
  );
  const setIsTicketTimeZoneIsResource = useGlobalStore(
    (state) => state.setIsTicketTimeZoneIsResource,
  );
  // const setMspTimeZone = useGlobalStore((state) => state.setMspTimeZone);
  const setMspCountryDetails = useGlobalStore(
    (state) => state.setMspCountryDetails,
  );
  const setPermissions = usePermissionStore((state) => state.setPermissions);
  const hasSubscriptionRw = usePermissionStore((state) =>
    state.hasPermissions([PERMISSIONS.CC.CC_SUBSCRIPTION_RW]),
  );
  const mspId = useGlobalStore((state) => state.mspId);
  const { data: resourceData } = useGetResourceById(resourceId || "");
  const { data: mspData } = useGetMspById(mspId || "");

  const { data: permissions } = useFetchCurrentResourceRoleAndPermission();
  const { data: generalConfigData } = useGetMspGeneralConfig(mspId);
  const { data: featureFlags, isLoading: isFeatureFlagsLoading } =
    useGetFeatureFlags();
  const { data: brandingData } = useGetBrandingLogo();

  // const { data: vanityUrlData, isLoading: isVanityUrlLoading } =
  //   useGetAllCertificates(mspId || "");

  const { data: userAuthData, isLoading: isUserAuthLoading } =
    useCheckUserAuth();
  const setSubscriptionData = useGlobalStore(
    (state) => state.setSubscriptionData,
  );
  const setBillingCycle = useGlobalStore((state) => state.setBillingCycle);
  const setFeatureFlags = useNonPersistantMainStore(
    (state) => state.setFeatureFlags,
  );
  const setWhatsNewFeatureFlagCreatedAt = useNonPersistantMainStore(
    (state) => state.setWhatsNewFeatureFlagCreatedAt,
  );
  const setIsEmailConfigured = useGlobalStore(
    (state) => state.setIsEmailConfigured,
  );
  const setMspFlags = useNonPersistantMainStore((state) => state.setMspFlags);
  const setMspDetailstoStore = useGlobalStore((state) => state.setMspDetails);
  const setMspCreatedAt = useGlobalStore((state) => state.setMspCreatedAt);
  const setBrandingLogoData = useGlobalStore(
    (state) => state.setBrandingLogoData,
  );

  const { data: emailList, isLoading: isEmailConfigLoading } =
    useFetchAllEmails();

  useEffect(() => {
    if (
      (Array.isArray(emailList) ? emailList : []).length > 0 &&
      emailList.findIndex(
        (item: { private_server?: boolean; is_primary?: boolean }) =>
          item.private_server && item.is_primary,
      ) !== -1
    ) {
      setIsEmailConfigured(true);
    } else {
      setIsEmailConfigured(false);
    }
  }, [isEmailConfigLoading, emailList]);

  useEffect(() => {
    if (brandingData) {
      const brandingLogoData: Record<string, string | null> = {};
      brandingData?.forEach((item: BrandingLogoResponse) => {
        if (item.branding_type === "logo") {
          // @ts-expect-error
          brandingLogoData.logoData = item;

          if (item?.url) {
            const faviconLink = document.querySelector("link[rel='icon']");
            if (faviconLink) {
              faviconLink.setAttribute("href", item?.url || "");
            }
          }
        }
        if (item.branding_type === "wordmark") {
          // @ts-expect-error
          brandingLogoData.wordmarkData = item;
        }
      });
      setBrandingLogoData({
        // @ts-expect-error
        logoData: brandingLogoData?.logoData?.url
          ? // @ts-ignore
            brandingLogoData?.logoData?.url
          : null,
        // @ts-expect-error
        wordmarkData: brandingLogoData?.wordmarkData?.url
          ? // @ts-ignore
            brandingLogoData?.wordmarkData?.url
          : null,
      });
    }
  }, [brandingData]);

  useEffect(() => {
    if (
      !!mspData &&
      !!resourceData &&
      !!generalConfigData &&
      !!featureFlags &&
      !!permissions
    ) {
      setPermissions(
        permissions?.resource_type,
        permissions?.permissions || [],
        permissions?.customers || [],
      );
      setMspDetails(mspData, generalConfigData);
      if (mspData.mspFlags) {
        setMspFlags(mspData.mspFlags);
      }
      setRefreshing(false);
      setFeatureFlags(featureFlags);
      setWhatsNewFeatureFlagCreatedAt(
        featureFlags?.find((item: FeatureProps) => item.name === "whats_new")
          ?.created_at,
      );
      setMspCreatedAt(mspData?.created_at);
      setTimeout(() => {
        setLoggedIn(true);
      }, 300);
    }
  }, [mspData, resourceData, generalConfigData, featureFlags, permissions]);

  const handleResourceRedirect = (
    vanityUrlData: Array<{
      status?: string;
      vanity_url_type?: string;
      domain_name?: string;
    }>,
    isDeskdayDomain: boolean,
  ) => {
    if (
      vanityUrlData &&
      vanityUrlData.length > 0 &&
      window.location.hostname !== "localhost"
    ) {
      const webDomains = vanityUrlData?.filter(
        (item: {
          status?: string;
          vanity_url_type?: string;
          domain_name?: string;
        }) =>
          item?.status === "ACTIVE" && item.vanity_url_type === "WEB_APP_URL",
      );
      if (webDomains?.length > 0) {
        const currentDomain = window.location.hostname;
        const currentPath =
          window.location.pathname +
          window.location.search +
          window.location.hash;
        let isMatchingDomain = false;
        try {
          const domainUrl = new URL(`https://${webDomains[0].domain_name}`);
          isMatchingDomain = domainUrl.hostname === currentDomain;

          if (!(isMatchingDomain || isDeskdayDomain)) {
            redirectToValidUrl({
              url: `https://${webDomains[0].domain_name}${currentPath}`,
              isVanity: true,
            });
          }
        } catch (e) {
          console.error("Error parsing domain URL:", e);
        }
      } else if (window.location.hostname !== "localhost") {
        if (!isDeskdayDomain) {
          redirectToValidUrl({
            url: `https://${import.meta.env.VITE_APP_AUTH_FRONT_URL}`,
            isVanity: false,
          });
        }
      }
    } else if (window.location.hostname !== "localhost") {
      if (!isDeskdayDomain) {
        redirectToValidUrl({
          url: `https://${import.meta.env.VITE_APP_AUTH_FRONT_URL}`,
          isVanity: false,
        });
      }
    }
  };

  const handleNonResourceRedirect = (
    vanityUrlData: Array<{
      status?: string;
      vanity_url_type?: string;
      domain_name?: string;
    }>,
  ) => {
    if (
      vanityUrlData &&
      vanityUrlData.length > 0 &&
      window.location.hostname !== "localhost"
    ) {
      const agentAppDomains = vanityUrlData?.filter(
        (item: {
          status?: string;
          vanity_url_type?: string;
          domain_name?: string;
        }) =>
          item?.status === "ACTIVE" && item.vanity_url_type === "AGENT_APP_URL",
      );
      if (agentAppDomains?.length > 0) {
        redirectToValidUrl({
          url: `https://${agentAppDomains[0].domain_name}`,
          isVanity: true,
        });
      } else {
        redirectToValidUrl({
          url: `https://${import.meta.env.VITE_APP_ITCONNECT_BASE_URL}`,
          isVanity: false,
        });
      }
    } else if (window.location.hostname !== "localhost") {
      redirectToValidUrl({
        url: `https://${import.meta.env.VITE_APP_ITCONNECT_BASE_URL}`,
        isVanity: false,
      });
    }
  };

  useEffect(() => {
    if (!userAuthData) return;
    let isResource = true;

    const vanityUrlData = userAuthData?.vanityMspDetails;
    const isDeskdayDomain = window.location.hostname.includes("deskday.ai");
    if (
      userAuthData?.customData?.userType ||
      userAuthData?.customData?.UserType
    ) {
      isResource =
        userAuthData?.customData?.userType === "Resource" ||
        userAuthData?.customData?.UserType === "Resource"
          ? true
          : false;
    }
    if (!isUserAuthLoading) {
      if (isResource) {
        handleResourceRedirect(vanityUrlData, isDeskdayDomain);
      } else {
        handleNonResourceRedirect(vanityUrlData);
      }
    }
  }, [userAuthData]);

  const redirectToValidUrl = async ({
    url,
    isVanity = false,
  }: {
    url: string;
    isVanity?: boolean;
  }) => {
    try {
      setIsRedirecting({
        isRedirecting: true,
        isVanity: isVanity,
        url: url,
      });
      // setTimeout(() => {
      //   signOut(`${window.location.origin}/login`);
      //   window.location.href = url;
      // }, 1000);
    } catch (error) {
      console.error("Error parsing domain URL:", error);
    }
  };

  const handleRedirectClick = (url: string) => {
    window.open(`${url}/login`, "_blank");
    signOut(`${window.location.origin}/login`);
  };

  // signout channel listener to signout from multiple tabs
  useEffect(() => {
    signoutBroadcast.onmessage = (ev) => {
      if (ev.data === "signout") {
        setLoggedIn(false);
        resetProfile();
        localStorage.clear();
        sessionStorage.clear();
        resetHeapIdentity();
        signOut(`${window.location.origin}/login`);
        // eslint-disable-next-line no-undef
        window.location.href = `${window.location.origin}/login`;
      }
    };
  }, []);

  function setMspDetails(
    mspData: Record<string, unknown>,
    generalConfig: Record<string, unknown>,
  ) {
    try {
      setSubscriptionData({
        subscriptionStatus: mspData?.status as string | null | undefined,
        cancelAtPeriodEnd: mspData?.cancel_at_period_end as
          | boolean
          | null
          | undefined,
        currentAccessBlockDate: mspData?.current_access_block_date as
          | string
          | null
          | undefined,
      });

      setBillingCycle(mspData?.billing_cycle as "weekly" | "monthly");
    } catch (error) {
      debugConsole(error);
    }

    let role: string = "SUPERADMIN";

    setDefaultData({
      type: "customer",
      value: mspData?.sub_tenant_id as string,
    });

    // set timezone,
    // if resource timezone is not empty then uses it
    // else if msp tomezone is not empty then uses it
    // else set browser timezone

    const timezone =
      resourceData?.time_zone &&
      resourceData?.time_zone !== null &&
      resourceData?.time_zone !== ""
        ? resourceData?.time_zone
        : mspData?.time_zone &&
            mspData?.time_zone !== null &&
            mspData?.time_zone !== ""
          ? mspData?.time_zone
          : dayjs.tz.guess();

    setIsResourceTimeZone(
      resourceData?.time_zone &&
        resourceData?.time_zone !== null &&
        resourceData?.time_zone !== "",
    );

    /// set date format based on company details. if time format is set then uses it or uses the default date format
    if (
      mspData.date_format &&
      mspData.date_format !== null &&
      mspData.date_format !== ""
    ) {
      setDateFormat(mspData.date_format as string);
    }

    setIsTicketTImeFormat24Hour(
      generalConfig?.is_time_format_is_12_hr as boolean | undefined,
    );
    setIsTicketTimeZoneIsResource(
      generalConfig?.is_time_zone_is_resources as boolean | undefined,
    );

    setGeneralConfig({
      defaultTicketBoard: generalConfig?.default_ticket_board as
        | string
        | null
        | undefined,
      defaultTicketBoardId: generalConfig?.ticket_board_id as
        | string
        | null
        | undefined,
      defaultProjectBoard: generalConfig?.project_board as
        | string
        | null
        | undefined,
      defaultProjectBoardId: generalConfig?.project_board_id as
        | string
        | null
        | undefined,
      defaultReplyMode: generalConfig?.default_reply_mode as
        | string
        | null
        | undefined,
      defaultTimeEntryNotesMode:
        generalConfig?.default_time_entry_notes_mode as
          | string
          | null
          | undefined,
      autoStartTimer: generalConfig?.auto_start_timer as
        | string
        | null
        | undefined,
    });

    const currentRole =
      Object.keys(RESOURCE_TYPES).find(
        //@ts-ignore
        (item: string) =>
          RESOURCE_TYPES[item as keyof typeof RESOURCE_TYPES] ===
          resourceData?.resource_type_id,
      ) || resourceData?.resource_type_name;

    if (currentRole) {
      role = currentRole;
    }

    setMspDetailstoStore({
      mspOwnerId: mspData?.msp_owner_id as string | null,
      name: `${emptyStringHandler(
        resourceData?.first_name,
      )} ${emptyStringHandler(resourceData?.last_name)}`,
      profilePictureUrl: resourceData?.profile_image,
      trialEndDate: mspData?.trial_end_date as string,
      sampleDataStatus: mspData?.sample_data as string,
      currency: {
        defaultCurrency: mspData?.default_currency as string,
        currencyName: mspData?.currency_name as string,
        currencyCode: mspData?.currency_code as string,
        symbol: mspData?.symbol as string,
        position: mspData?.position as string,
      },
      role: role as "SUPERADMIN" | "ADMIN" | "RESOURCE" | null,
      timezone: timezone || "America/Toronto",
      companyName: mspData?.company_name as string,
      isBillableDefault: mspData?.is_billable_default as boolean,
      firstName: resourceData?.first_name as string,
      middleName: resourceData?.middle_name as string,
      lastName: resourceData?.last_name as string,
      isHelenaActive: !!mspData?.helena_enabled as boolean,
      brandName: mspData?.brand_name as string,
      mspEmail: mspData?.business_email as string,
      isEnterprise: mspData?.pricing_type === "tiered",
    });

    setMspCountryDetails({
      countryDetails: {
        countryName: mspData?.country_name as string,
        countryCode: mspData?.country as string,
        stateName: mspData?.state_name as string,
        stateCode: mspData?.state as string,
        cityName: mspData?.city as string,
      },
      mspTimezone: mspData?.time_zone as string,
    });
  }

  useEffect(() => {
    if (intercomHashData && resourceData) {
      activateIntercom(intercomHashData);
      addSentryScope();
      addMixpanelData({ resourceData, mspData });
      addHeapData({ resourceData, mspData });
    }
  }, [intercomHashData, resourceData]);

  // adding data to heap
  const addHeapData = ({
    resourceData,
    mspData,
  }: {
    resourceData: Record<string, unknown>;
    mspData: Record<string, unknown>;
  }) => {
    identifyHeapUser(resourceData?.mapping_id as string);
    setHeapUserProperties({
      email: resourceData?.email as string,
      name: `${resourceData?.first_name as string} ${resourceData?.last_name as string}`,
      resourceType: resourceData?.resource_type_name as string,
    });
    setHeapAccountProperties({
      companyName: mspData?.company_name as string,
      businessEmail: mspData?.business_email as string,
      mspId: mspData?.mapping_id as string,
    });
  };

  // adding data to mixpanel
  const addMixpanelData = ({
    resourceData,
    mspData,
  }: {
    resourceData: Record<string, unknown>;
    mspData: Record<string, unknown>;
  }) => {
    mixpanel.identify(resourceData?.mapping_id as string);
    mixpanel.people.set({
      $email: resourceData?.email,
      $name: `${resourceData?.first_name as string} ${resourceData?.last_name as string}`,
      resourceType: resourceData?.resource_type_name,
      mspId: mspData?.mapping_id,
      mspEmail: mspData?.business_email,
      msp: mspData?.company_name,
    });
  };
  // adding user scope to sentry user
  const addSentryScope = () => {
    Sentry.setUser({ email: resourceData.email, id: resourceData?.mapping_id });
  };
  const activateIntercom = (data: { hash?: string }) => {
    try {
      setTimeout(() => {
        //@ts-expect-error
        window.Intercom("boot", {
          api_base: "https://api-iam.intercom.io",
          app_id: "vjbuario",
          user_id: resourceData?.mapping_id,
          email: resourceData.email,
          user_hash: data?.hash, // HMAC using SHA-256  };
        });
        const currentRole = Object.keys(RESOURCE_TYPES).find(
          //@ts-ignore
          (item: string) =>
            RESOURCE_TYPES[item as keyof typeof RESOURCE_TYPES] ===
            resourceData.resource_type_id,
        );
        // adding role to intercom
        addAttributeToIntercom({ event: `${currentRole}` });
      }, 3400);

      // only relaod intercom if user is on home page
      const locationArr = window.location.href.split("/");
      if (locationArr[locationArr.length - 1] === "home") {
        setTimeout(() => {
          //@ts-expect-error
          window.Intercom("update");
        }, 64000);
      }
    } catch (error) {
      debugConsole(error);
    }
  };

  const [isDataRefreshing, setIsDataRefreshing] = useState(true);

  useEffect(() => {
    setTimeout(() => {
      setIsDataRefreshing(false);
    }, 3200);
  }, []);

  const checkSubscriptionStatus = () => {
    // check if tried period is over
    if (dayjs(mspData?.server_time).isBefore(dayjs(mspData?.trial_end_date))) {
      return true;
    }
    // need to check if current access block date is over
    // sometimes status is not updated by stripe
    if (
      dayjs(mspData?.server_time).isAfter(
        dayjs(mspData?.current_access_block_date),
      )
    ) {
      return false;
    }
    if (mspData?.status === "active") {
      return true;
    }
    if (
      dayjs(mspData?.server_time).isBefore(
        dayjs(mspData?.current_access_block_date),
      )
    ) {
      return true;
    }
    return false;
  };

  const featureFlagsFromStore = useNonPersistantMainStore(
    (state) => state.featureFlags,
  );
  return (
    <>
      <CustomHelmet />
      {!loggedIn || isDataRefreshing || !mspData || isFeatureFlagsLoading ? (
        <Box
          sx={{
            position: "absolute",
            top: isRedirecting.isRedirecting ? 0 : "225px",
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "#FFF",
            zIndex: 1000000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {isRedirecting.isRedirecting ? (
            <RedirectToDomainLoader
              heading="User accounts aren’t supported on the DeskDay Portal"
              tagline="You tried signing in to the DeskDay web portal with a user account. User accounts can only be used on the IT-Connect Customer Web Portal."
              isVanity={isRedirecting.isVanity}
              showCta={true}
              onPrimaryButtonClick={() =>
                handleRedirectClick(isRedirecting.url)
              }
              primaryButtonLabel="Open IT-Connect Portal"
              onSecondaryButtonClick={() =>
                signOut(`${window.location.origin}/login`)
              }
              secondaryButtonLabel="Logout"
            />
          ) : (
            <Box sx={{ width: "520px", height: "520px" }}>
              <RefreshComponent />
            </Box>
          )}
        </Box>
      ) : isRedirecting?.isRedirecting ? (
        <RedirectToDomainLoader isVanity={isRedirecting.isVanity} />
      ) : (
        <>
          {checkSubscriptionStatus() ? (
            // <>
            //   {sampleDataStatus === "pending" ? (
            //     <SampleDataHomeScreen />
            //   ) : (
            //     <>
            //       {children}
            //       {/* <MainPageBanner /> */}
            //     </>
            //   )}
            // </>
            <>
              {featureFlagsFromStore?.show_onboarding_screen === true &&
              role === "SUPERADMIN" ? (
                <Onboarding />
              ) : (
                <>
                  {/* <MainPageBanner /> */}
                  {children}
                </>
              )}
            </>
          ) : (
            <>
              {hasSubscriptionRw ? (
                <Stack>
                  {/* MainLayout isn't mounted here, so add the theme's global
                      styles (hidden scrollbars, border-box) it normally brings */}
                  <CssBaseline />
                  <Stack
                    px={"32px"}
                    py="6px"
                    direction={"row"}
                    justifyContent={"flex-end"}
                  >
                    <TopBarProfileButton />
                  </Stack>
                  <ExpiredSubscriptionBanner />
                  <PricingPageFrame hasBannerAbove>
                    <OverviewPricing />
                  </PricingPageFrame>
                </Stack>
              ) : (
                loggedIn && <NoSubscriptionForNonSuperAdmin />
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

const NoSubscriptionForNonSuperAdmin = () => {
  const theme = useTheme();
  const { mutateAsync: logoutAccount, isLoading: isLogoutLoading } =
    useLogoutAccount();
  const { signOut } = useLogto();

  const signOutHandler = async () => {
    try {
      const res = await logoutAccount();
      resetAllStores();
      if (
        res &&
        res?.data &&
        res?.data?.success === true &&
        res?.data?.statusCode === 200
      ) {
        signoutBroadcast.postMessage("signout");
        // clear sentry scope
        Sentry.setUser(null);
        sessionStorage.clear();
        localStorage.clear();
      }
      resetHeapIdentity();
      // eslint-disable-next-line no-undef
      window.location.href = `${window.location.origin}/login`;
    } catch (e: unknown) {
      resetAllStores();
      if ((e as Error)?.message === "unauthorized") {
        signoutBroadcast.postMessage("signout");
        sessionStorage.clear();
        localStorage.clear();
        resetHeapIdentity();
        signOut(`${window.location.origin}/login`);
        // eslint-disable-next-line no-undef
        window.location.href = `${window.location.origin}/login`;
      }
    }
  };
  return (
    <Stack
      sx={{
        width: "100vw",
        height: "100vh",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <ErrorLayout
        leftSide={
          <Stack sx={{ maxWidth: "350px", gap: "24px" }}>
            <Typography variant="h4">Account Temporarily Suspended</Typography>
            <Typography
              variant="body-large"
              color={theme.palette.lightBg.medium}
            >
              Please contact your admin for plan renewal and account
              reactivation
            </Typography>
            <Button loading={isLogoutLoading} onClick={signOutHandler}>
              Logout
            </Button>
          </Stack>
        }
        errorImg={GearImgSvg}
      />
    </Stack>
  );
};

const RefreshComponent = () => {
  return <Lottie animationData={refreshLottie} loop={true} />;
};
